
import React, { useState, useEffect, useMemo, useRef } from "react";
import {
  Box,
  Typography,
  Button,
  Card,
  CardContent,
  CardMedia,
  Grid,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  CircularProgress,
  Alert,
  AppBar,
  Toolbar,
  Tabs,
  Tab,
  Pagination,
  Menu,
  MenuItem,
  ListItemIcon,
  ListItemText,
  Chip,
  useMediaQuery,
  useTheme,
  Link as MuiLink,
} from "@mui/material";
import {
  Add,
  Close,
  MoreVert,
  Edit,
  Delete,
  Article,
  Image as ImageIcon,
  CheckCircle,
  Cancel,
  ArrowBackIos,
  ArrowForwardIos,
  Visibility,
  Link as LinkIcon,
} from "@mui/icons-material";
import { URL as API_URL } from "../constants/url";
import colors from "../constants/colors";

const Articles = ({ adminData, searchText }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const isTablet = useMediaQuery(theme.breakpoints.down("md"));

  const [articles, setArticles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [dialogLoading, setDialogLoading] = useState(false);
  const [openDialog, setOpenDialog] = useState(false);
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    severity: "success",
  });
  const [activeTab, setActiveTab] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [menuAnchor, setMenuAnchor] = useState(null);
  const [selectedArticle, setSelectedArticle] = useState(null);
  const [editDialogOpen, setEditDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [editingArticle, setEditingArticle] = useState(null);
  const [imageIndex, setImageIndex] = useState({});
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedArticleDetail, setSelectedArticleDetail] = useState(null);

  const userId = adminData?._id;
  const ITEMS_PER_PAGE = 9;

  const [formData, setFormData] = useState({
    title: "",
    description: "",
    link: "",
  });

  const [editFormData, setEditFormData] = useState({
    title: "",
    description: "",
    link: "",
  });

  // Separate states for image previews
  const [imagePreviews, setImagePreviews] = useState([]);
  const [editImagePreviews, setEditImagePreviews] = useState([]);

  // Filter articles based on search text
  const filteredArticles = useMemo(() => {
    let filtered = articles;

    // Apply search filter
    if (searchText && searchText.trim() !== "") {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(
        (article) =>
          article.title?.toLowerCase().includes(searchLower) ||
          article.description?.toLowerCase().includes(searchLower) ||
          article.link?.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [articles, searchText]);

  const paginatedArticles = useMemo(() => {
    const startIndex = (page - 1) * ITEMS_PER_PAGE;
    return filteredArticles.slice(startIndex, startIndex + ITEMS_PER_PAGE);
  }, [filteredArticles, page]);

  useEffect(() => {
    setTotalPages(Math.ceil(filteredArticles.length / ITEMS_PER_PAGE));
    setPage(1);
  }, [filteredArticles]);

  const fetchArticles = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams();
      if (searchText && searchText.trim() !== "") {
        queryParams.append("search", searchText);
      }

      const res = await fetch(
        `${API_URL}/api/charcha/get-all-articles?${queryParams}`
      );
      const data = await res.json();
      setArticles(data?.data || []);
    } catch (error) {
      console.error("Error fetching articles:", error);
      showAlert("Error fetching articles", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchArticles();
  }, [searchText]);

  useEffect(() => {
    setPage(1);
  }, [searchText]);

  const showAlert = (message, severity = "success") => {
    setAlert({ open: true, message, severity });
  };

  const handleCloseAlert = () => {
    setAlert({ open: false, message: "", severity: "success" });
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((p) => ({ ...p, [name]: value }));
  };

  const handleEditInputChange = (e) => {
    const { name, value } = e.target;
    setEditFormData((p) => ({ ...p, [name]: value }));
  };

  const handleImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Limit to 5 images
      const filesToAdd = files.slice(0, 5 - imagePreviews.length);
      if (filesToAdd.length > 0) {
        setFormData((prev) => ({
          ...prev,
          images: [...(prev.images || []), ...filesToAdd],
        }));

        // Create previews for new images
        const newPreviews = filesToAdd.map((file) => URL.createObjectURL(file));
        setImagePreviews((prev) => [...prev, ...newPreviews]);
      }
    }
  };

  const handleEditImageChange = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      // Limit to 5 images total (existing + new)
      const filesToAdd = files.slice(0, 5 - editImagePreviews.length);
      if (filesToAdd.length > 0) {
        setEditFormData((prev) => ({
          ...prev,
          images: [...(prev.images || []), ...filesToAdd],
        }));

        // Create previews for new images
        const newPreviews = filesToAdd.map((file) => URL.createObjectURL(file));
        setEditImagePreviews((prev) => [...prev, ...newPreviews]);
      }
    }
  };

  const removeImage = (index) => {
    setFormData((prev) => ({
      ...prev,
      images: prev.images?.filter((_, i) => i !== index) || [],
    }));
    // Also remove the preview URL
    URL.revokeObjectURL(imagePreviews[index]);
    setImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const removeEditImage = (index) => {
    setEditFormData((prev) => ({
      ...prev,
      images: prev.images?.filter((_, i) => i !== index) || [],
    }));
    // Also remove the preview URL
    URL.revokeObjectURL(editImagePreviews[index]);
    setEditImagePreviews((prev) => prev.filter((_, i) => i !== index));
  };

  const handleNextImage = (id, total) => {
    setImageIndex((prev) => ({
      ...prev,
      [id]: ((prev[id] || 0) + 1) % total,
    }));
  };

  const handlePrevImage = (id, total) => {
    setImageIndex((prev) => ({
      ...prev,
      [id]: ((prev[id] || 0) - 1 + total) % total,
    }));
  };

  const handleOpenDetail = (article) => {
    setSelectedArticleDetail(article);
    setDetailModalOpen(true);
  };

  const resetForm = () => {
    setFormData({ title: "", description: "", link: "", images: [] });
    // Clean up preview URLs
    imagePreviews.forEach((url) => URL.revokeObjectURL(url));
    setImagePreviews([]);
  };

  const resetEditForm = () => {
    setEditFormData({ title: "", description: "", link: "", images: [] });
    // Clean up preview URLs
    editImagePreviews.forEach((url) => URL.revokeObjectURL(url));
    setEditImagePreviews([]);
    setEditingArticle(null);
  };

  // Clean up object URLs when component unmounts
  useEffect(() => {
    return () => {
      imagePreviews.forEach((url) => URL.revokeObjectURL(url));
      editImagePreviews.forEach((url) => URL.revokeObjectURL(url));
    };
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setDialogLoading(true);

    try {
      const submitData = new FormData();
      submitData.append("title", formData.title);
      submitData.append("description", formData.description);
      submitData.append("link", formData.link || ""); // Add link field
      submitData.append("userId", userId);

      // Append all images - USE articleImages (backend expects this)
      formData.images?.forEach((image) => {
        submitData.append("articleImages", image);
      });

      const res = await fetch(`${API_URL}/api/charcha/post-article`, {
        method: "POST",
        body: submitData,
      });

      const result = await res.json();

      if (result.success) {
        setOpenDialog(false);
        resetForm();
        showAlert("Article posted successfully!");
        fetchArticles();
      } else {
        showAlert(result.message, "error");
      }
    } catch (error) {
      showAlert("Error posting article", "error");
    } finally {
      setDialogLoading(false);
    }
  };

  const handleEditSubmit = async (e) => {
    e.preventDefault();
    setDialogLoading(true);

    try {
      const submitData = new FormData();
      submitData.append("title", editFormData.title);
      submitData.append("description", editFormData.description);
      submitData.append("link", editFormData.link || ""); // Add link field
      submitData.append("userId", userId);

      // Append new images if any - USE articleImages (backend expects this)
      editFormData.images?.forEach((image) => {
        submitData.append("articleImages", image);
      });

      const res = await fetch(
        `${API_URL}/api/charcha/edit-article/${editingArticle._id}`,
        {
          method: "POST",
          body: submitData,
        }
      );

      const result = await res.json();

      if (result.success) {
        setEditDialogOpen(false);
        resetEditForm();
        showAlert("Article updated successfully!");
        fetchArticles();
      } else {
        showAlert(result.message, "error");
      }
    } catch (error) {
      showAlert("Error updating article", "error");
    } finally {
      setDialogLoading(false);
    }
  };

  const handleDeleteArticle = async () => {
    if (!selectedArticle || !selectedArticle._id) {
      console.error(
        "Cannot delete: selectedArticle is null or missing _id",
        selectedArticle
      );
      showAlert("Error: No article selected for deletion", "error");
      setDeleteDialogOpen(false);
      setDialogLoading(false);
      return;
    }

    setDialogLoading(true);
    try {
      const articleIdToDelete = selectedArticle._id;

      const res = await fetch(
        `${API_URL}/api/charcha/delete-articles/${articleIdToDelete}/${userId}`,
        {
          method: "DELETE",
        }
      );

      const result = await res.json();

      if (result.success) {
        setDeleteDialogOpen(false);
        setMenuAnchor(null);
        setSelectedArticle(null);
        showAlert("Article deleted successfully!");
        fetchArticles();
      } else {
        showAlert(result.message, "error");
      }
    } catch (error) {
      console.error("❌ Delete error:", error);
      showAlert("Error deleting article", "error");
    } finally {
      setDialogLoading(false);
    }
  };

  const handleMenuOpen = (event, article) => {
    setMenuAnchor(event.currentTarget);
    setSelectedArticle(article);
  };

  const handleMenuClose = () => {
    setMenuAnchor(null);
  };

  const handleEditClick = () => {
    if (selectedArticle && selectedArticle._id) {
      setEditingArticle(selectedArticle);
      setEditFormData({
        title: selectedArticle.title || "",
        description: selectedArticle.description || "",
        link: selectedArticle.link || "", // Add link field
        images: [],
      });
      setEditImagePreviews([]);
      setEditDialogOpen(true);
      setMenuAnchor(null);
    } else {
      console.error("No article selected for editing");
      showAlert("No article selected for editing", "error");
      setMenuAnchor(null);
    }
  };

  const handleDeleteClick = () => {
    if (selectedArticle && selectedArticle._id) {
      setDeleteDialogOpen(true);
      setMenuAnchor(null);
    } else {
      console.error("No article selected for deletion");
      showAlert("No article selected for deletion", "error");
      setMenuAnchor(null);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  const renderArticleCard = (article) => {
    const imgs =
      article.images && article.images.length > 0
        ? article.images.map((img) => `${API_URL}${img}`)
        : [];
    const idx = imageIndex[article._id] || 0;
    const currentImage = imgs.length > 0 ? imgs[idx] : null;

    return (
      <Card
        sx={{
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          borderRadius: 3,
          transition: "0.3s",
          height: { xs: "auto", sm: 220 },
          width: "100%",
          position: "relative",
          overflow: "hidden",
          "&:hover": {
            transform: "translateY(-5px)",
            boxShadow: "0 8px 20px rgba(0,0,0,0.12)",
            "& .article-actions": {
              opacity: 1,
            },
          },
        }}
      >
        {/* Action Menu Button */}
        <IconButton
          className="article-actions"
          sx={{
            position: "absolute",
            top: 8,
            right: 8,
            bgcolor: "rgba(255,255,255,0.9)",
            opacity: { xs: 1, sm: 0 },
            transition: "opacity 0.3s ease",
            zIndex: 10,
            "&:hover": {
              bgcolor: "white",
            },
          }}
          onClick={(e) => handleMenuOpen(e, article)}
        >
          <MoreVert />
        </IconButton>

        {/* Article Image with Carousel */}
        {currentImage ? (
          <Box
            sx={{
              width: { xs: "100%", sm: 250 },
              height: { xs: 200, sm: "100%" },
              minWidth: { xs: "100%", sm: 250 },
              maxWidth: { xs: "100%", sm: 250 },
              position: "relative",
              overflow: "hidden",
              flexShrink: 0,
            }}
          >
            <CardMedia
              component="img"
              sx={{
                width: "100%",
                height: "100%",
                objectFit: "cover",
              }}
              image={currentImage}
              alt={article.title}
            />

            {/* Carousel Navigation */}
            {imgs.length > 1 && (
              <>
                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: 8,
                    transform: "translateY(-50%)",
                    color: colors.green,
                    backgroundColor: "rgba(255, 255, 255, 0.25)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.4)",
                      transform: "translateY(-50%) scale(1.15)",
                      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.25)",
                      color: colors.darkGreen,
                    },
                    transition: "all 0.3s ease",
                    width: "36px",
                    height: "36px",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handlePrevImage(article._id, imgs.length);
                  }}
                >
                  <ArrowBackIos
                    fontSize="small"
                    sx={{
                      fontSize: "18px",
                      marginLeft: "2px",
                      filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
                    }}
                  />
                </IconButton>

                <IconButton
                  size="small"
                  sx={{
                    position: "absolute",
                    top: "50%",
                    right: 8,
                    transform: "translateY(-50%)",
                    color: colors.green,
                    backgroundColor: "rgba(255, 255, 255, 0.25)",
                    backdropFilter: "blur(8px)",
                    border: "1px solid rgba(255, 255, 255, 0.3)",
                    boxShadow: "0 2px 8px rgba(0, 0, 0, 0.15)",
                    "&:hover": {
                      backgroundColor: "rgba(255, 255, 255, 0.4)",
                      transform: "translateY(-50%) scale(1.15)",
                      boxShadow: "0 4px 16px rgba(0, 0, 0, 0.25)",
                      color: colors.darkGreen,
                    },
                    transition: "all 0.3s ease",
                    width: "36px",
                    height: "36px",
                  }}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleNextImage(article._id, imgs.length);
                  }}
                >
                  <ArrowForwardIos
                    fontSize="small"
                    sx={{
                      fontSize: "18px",
                      filter: "drop-shadow(0 1px 2px rgba(0,0,0,0.2))",
                    }}
                  />
                </IconButton>
              </>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              width: { xs: "100%", sm: 250 },
              height: { xs: 200, sm: "100%" },
              minWidth: { xs: "100%", sm: 250 },
              maxWidth: { xs: "100%", sm: 250 },
              bgcolor: colors.lightGray,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              flexDirection: "column",
              flexShrink: 0,
            }}
          >
            <ImageIcon sx={{ fontSize: 48, color: colors.darkGray }} />
            <Typography variant="body2" color={colors.darkGray}>
              No Image
            </Typography>
          </Box>
        )}

        {/* Card Content */}
        <Box
          sx={{
            flex: 1,
            display: "flex",
            flexDirection: "column",
            minWidth: 0,
            width: "100%",
            position: "relative",
          }}
        >
          <CardContent
            sx={{
              p: { xs: 2, sm: 3 },
              flex: 1,
              display: "flex",
              flexDirection: "column",
              width: "100%",
              minWidth: 0,
              pb: { xs: 7, sm: 7 },
              "&:last-child": {
                pb: { xs: 7, sm: 7 },
              },
            }}
          >
            {/* Title */}
            <Box
              sx={{
                mb: { xs: 1, sm: 2 },
                minHeight: "auto",
                overflow: "hidden",
                width: "100%",
              }}
            >
              <Typography
                variant="h6"
                sx={{
                  fontWeight: 600,
                  display: "-webkit-box",
                  WebkitLineClamp: { xs: 2, sm: 2 },
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.4,
                  fontSize: { xs: "1rem", sm: "1.1rem" },
                  width: "100%",
                }}
              >
                {article.title}
              </Typography>
            </Box>

            {/* Description container */}
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                mt: { xs: 0, sm: 0 },
                minHeight: { xs: "auto", sm: "auto" },
                width: "100%",
                overflow: "hidden",
              }}
            >
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  display: "-webkit-box",
                  WebkitLineClamp: { xs: 3, sm: 4 },
                  WebkitBoxOrient: "vertical",
                  overflow: "hidden",
                  lineHeight: 1.5,
                  flex: 1,
                  minHeight: "auto",
                  width: "100%",
                  wordBreak: "break-word",
                  fontSize: { xs: "0.875rem", sm: "0.9rem" },
                }}
              >
                {article.description}
              </Typography>
            </Box>
          </CardContent>

          {/* Fixed Footer with View Full Article Button and Date */}
          <Box
            sx={{
              position: "absolute",
              bottom: 0,
              left: 0,
              right: 0,
              backgroundColor: "white",
              borderTop: `1px solid ${colors.lightGray}`,
              p: { xs: 1.5, sm: 2 },
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              minHeight: "auto",
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Button
              onClick={() => handleOpenDetail(article)}
              size="small"
              startIcon={<Visibility />}
              sx={{
                color: colors.green,
                fontWeight: 600,
                textTransform: "none",
                fontSize: "0.875rem",
                "&:hover": {
                  backgroundColor: "rgba(76, 175, 80, 0.1)",
                },
              }}
            >
              View Full Article
            </Button>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{
                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                flexShrink: 0,
              }}
            >
              {formatDate(article.createdAt)}
            </Typography>
          </Box>
        </Box>
      </Card>
    );
  };

  // Custom Alert Component
  const CustomAlert = ({ open, severity, message, onClose }) => {
    if (!open) return null;

    const isSuccess = severity === "success";
    const borderColor = isSuccess ? colors.limeGreen : colors.deleteBg;
    const iconColor = isSuccess ? colors.darkGreen : colors.red;
    const buttonColor = isSuccess ? colors.darkGreen : colors.red;
    const buttonHoverColor = isSuccess ? colors.green : colors.darkRed;

    return (
      <Box
        sx={{
          position: "fixed",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 9999,
          width: "auto",
          minWidth: { xs: "90vw", sm: 400 },
          maxWidth: "90vw",
        }}
      >
        <Box
          sx={{
            backgroundColor: "white",
            border: `3px solid ${borderColor}`,
            borderRadius: 4,
            padding: 0,
            boxShadow: "0 10px 40px rgba(0,0,0,0.25)",
            overflow: "hidden",
            display: "flex",
            flexDirection: "column",
          }}
        >
          {/* Header Strip */}
          <Box
            sx={{
              backgroundColor: borderColor,
              padding: { xs: "12px 16px", sm: "16px 24px" },
              display: "flex",
              alignItems: "center",
              gap: 2,
            }}
          >
            {/* Icon */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                width: { xs: 28, sm: 36 },
                height: { xs: 28, sm: 36 },
                borderRadius: "50%",
                backgroundColor: "white",
                flexShrink: 0,
              }}
            >
              {isSuccess ? (
                <CheckCircle
                  sx={{ color: iconColor, fontSize: { xs: 18, sm: 22 } }}
                />
              ) : (
                <Cancel
                  sx={{ color: iconColor, fontSize: { xs: 18, sm: 22 } }}
                />
              )}
            </Box>

            {/* Title */}
            <Typography
              sx={{
                fontSize: { xs: "1.1rem", sm: "1.3rem" },
                fontWeight: 700,
                color: "white",
                textTransform: "uppercase",
                letterSpacing: "0.5px",
              }}
            >
              {isSuccess ? "Success" : "Error"}
            </Typography>
          </Box>

          {/* Message Content */}
          <Box
            sx={{
              padding: { xs: "16px", sm: "24px" },
              display: "flex",
              flexDirection: "column",
              gap: { xs: 2, sm: 3 },
            }}
          >
            {/* Message Text */}
            <Typography
              sx={{
                fontSize: { xs: "1rem", sm: "1.1rem" },
                fontWeight: 500,
                color: colors.darkBlack,
                lineHeight: 1.5,
                textAlign: "center",
              }}
            >
              {message}
            </Typography>

            {/* OK Button */}
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <Button
                variant="contained"
                onClick={onClose}
                sx={{
                  backgroundColor: buttonColor,
                  color: "white",
                  fontWeight: 700,
                  borderRadius: 3,
                  px: 4,
                  py: 1,
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  minWidth: 120,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                  "&:hover": {
                    backgroundColor: buttonHoverColor,
                    transform: "translateY(-2px)",
                    boxShadow: "0 6px 16px rgba(0,0,0,0.2)",
                  },
                  "&:active": {
                    transform: "translateY(0)",
                  },
                  transition: "all 0.3s ease",
                }}
              >
                OK
              </Button>
            </Box>
          </Box>
        </Box>
      </Box>
    );
  };

  return (
    <Box
      sx={{
        height: "100vh", // CHANGE to 100vh
        display: "flex",
        flexDirection: "column",
        backgroundColor: colors.darkWhite,
        overflow: "hidden", // ADD THIS
      }}
    >
      {/* Custom Alert */}
      <CustomAlert
        open={alert.open}
        severity={alert.severity}
        message={alert.message}
        onClose={handleCloseAlert}
      />

      {/* AppBar with Tabs and Add Article button */}
      <AppBar
        position="sticky"
        sx={{
          bgcolor: "white",
          color: colors.darkBlack,
          boxShadow: "0 2px 10px rgba(0,0,0,0.1)",
        }}
      >
        <Toolbar
          sx={{
            justifyContent: "space-between",
            gap: 2,
            alignItems: "center",
            minHeight: "64px !important",
            px: { xs: 2, sm: 3 },
          }}
        >
          {/* Single Tab for Articles */}
          <Box
            sx={{
              flex: 1,
              minWidth: 0,
              overflow: "hidden",
              mr: 2,
            }}
          >
            <Tabs
              value={0}
              sx={{
                minHeight: "48px",
                "& .MuiTabs-indicator": {
                  backgroundColor: colors.green,
                  height: 3,
                  borderRadius: 3,
                },
                "& .MuiTab-root": {
                  fontWeight: 600,
                  textTransform: "none",
                  fontSize: { xs: "0.8rem", sm: "0.9rem", md: "1rem" },
                  minWidth: "auto",
                  px: { xs: 1, sm: 3 },
                  minHeight: "48px",
                  whiteSpace: "nowrap",
                },
                "& .MuiTab-root.Mui-selected": {
                  color: colors.green,
                },
              }}
            >
              <Tab
                icon={
                  <Article
                    sx={{ mr: 1, fontSize: { xs: "18px", sm: "20px" } }}
                  />
                }
                iconPosition="start"
                label={isMobile ? "Articles" : "All Articles"}
              />
            </Tabs>
          </Box>

          {/* Add Article Button */}
          <Button
            variant="contained"
            startIcon={<Add />}
            onClick={() => setOpenDialog(true)}
            sx={{
              background: colors.darkGreen,
              "&:hover": { background: colors.darkGreen },
              fontSize: { xs: "0.75rem", sm: "0.875rem", md: "0.9rem" },
              padding: { xs: "6px 12px", sm: "8px 16px", md: "10px 20px" },
              minWidth: { xs: "auto", sm: "140px" },
              "& .MuiButton-startIcon": {
                marginRight: { xs: "4px", sm: "8px" },
              },
              borderRadius: 4,
            }}
          >
            {isMobile ? "Add" : "Add Article"}
          </Button>
        </Toolbar>
      </AppBar>

          <Box
        sx={{
          flex: 1, 
          minHeight: 0,
          overflowY: 'auto',
          overflowX: 'hidden', 
          p: { xs: 2, sm: 3 },
        }}
      >
        {/* Search Results Info */}
        {searchText && (
          <Box sx={{ mb: 2 }}>
            <Typography
              variant="body1"
              color="text.secondary"
              sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}
            >
              Search results for: <strong>"{searchText}"</strong>
              {filteredArticles.length > 0 &&
                ` (${filteredArticles.length} found)`}
            </Typography>
          </Box>
        )}

        {/* Loading State */}
        {loading ? (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              height: "50vh",
            }}
          >
            <Box sx={{ textAlign: "center" }}>
              <CircularProgress
                size={60}
                sx={{
                  color: colors.green,
                  mb: 2,
                }}
              />
              <Typography
                variant="h6"
                color="text.secondary"
                sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
              >
                {searchText ? "Searching articles..." : "Loading articles..."}
              </Typography>
            </Box>
          </Box>
        ) : (
          <Grid container spacing={2}>
            {paginatedArticles.length > 0 ? (
              paginatedArticles.map((article) => (
                <Grid
                  item
                  xs={12}
                  key={article._id}
                  sx={{ display: "flex", width: "100%" }}
                >
                  {renderArticleCard(article)}
                </Grid>
              ))
            ) : (
              <Grid item xs={12}>
                <Box sx={{ textAlign: "center", py: 8 }}>
                  <Article
                    sx={{
                      fontSize: { xs: 48, sm: 64 },
                      color: colors.midGray,
                      mb: 2,
                    }}
                  />
                  <Typography
                    variant="h6"
                    color="text.secondary"
                    sx={{ fontSize: { xs: "1rem", sm: "1.25rem" } }}
                  >
                    {searchText
                      ? "No articles match your search"
                      : "No articles found"}
                  </Typography>
                </Box>
              </Grid>
            )}
          </Grid>
        )}

        {totalPages > 1 && !loading && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              mt: 4,
            }}
          >
            <Pagination
              count={totalPages}
              page={page}
              onChange={(e, v) => setPage(v)}
              color="primary"
              size={isMobile ? "small" : "medium"}
              sx={{
                "& .MuiPaginationItem-root": {
                  color: "#2c3e50",
                  fontSize: { xs: "0.9rem", sm: "1.1rem" },
                  fontWeight: 500,
                  minWidth: { xs: "32px", sm: "42px" },
                  height: { xs: "32px", sm: "42px" },
                  margin: "0 2px",
                  border: "1px solid #e0e0e0",
                  borderRadius: "8px",
                  transition: "all 0.3s ease",
                  "&:hover": {
                    backgroundColor: "#f5f5f5",
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 8px rgba(0,0,0,0.1)",
                  },
                },
                "& .MuiPaginationItem-page": {
                  "&.Mui-selected": {
                    backgroundColor: colors.darkGreen,
                    color: colors.white,
                    fontWeight: 600,
                    fontSize: { xs: "1rem", sm: "1.2rem" },
                    border: `2px solid ${colors.darkGreen}`,
                    boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                    "&:hover": {
                      backgroundColor: colors.green,
                      transform: "translateY(-2px)",
                      boxShadow: "0 6px 16px rgba(76, 175, 80, 0.4)",
                    },
                  },
                },
                "& .MuiPaginationItem-ellipsis": {
                  fontSize: { xs: "1rem", sm: "1.2rem" },
                  fontWeight: 600,
                  color: "#666",
                },
                "& .MuiPaginationItem-previousNext": {
                  border: `2px solid ${colors.green}`,
                  backgroundColor: "transparent",
                  color: colors.green,
                  fontWeight: 600,
                  "&:hover": {
                    backgroundColor: colors.green,
                    color: colors.white,
                    transform: "translateY(-2px)",
                    boxShadow: "0 4px 12px rgba(76, 175, 80, 0.3)",
                  },
                  "&.Mui-disabled": {
                    border: "1px solid #e0e0e0",
                    color: "#bdbdbd",
                    backgroundColor: "#fafafa",
                  },
                },
              }}
            />
          </Box>
        )}
      </Box>

      {/* Article Detail Modal */}
      <Dialog
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        maxWidth="lg"
        fullWidth
        fullScreen={isMobile}
        sx={{
          "& .MuiDialog-paper": {
            maxHeight: "90vh",
          },
        }}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              borderBottom: `2px solid ${colors.lightGray}`,
              pb: 2,
            }}
          >
            <Typography
              variant="h4"
              fontWeight="bold"
              sx={{
                fontSize: { xs: "1.5rem", sm: "2rem" },
                color: colors.darkGreen,
                lineHeight: 1.2,
              }}
            >
              {selectedArticleDetail?.title}
            </Typography>
            <IconButton
              onClick={() => setDetailModalOpen(false)}
              size={isMobile ? "small" : "medium"}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent sx={{ p: { xs: 2, sm: 4 } }}>
          {/* Images Gallery */}
          {selectedArticleDetail?.images &&
            selectedArticleDetail.images.length > 0 && (
              <Box sx={{ mb: 4 }}>
                <Grid container spacing={2}>
                  {selectedArticleDetail.images.map((img, index) => (
                    <Grid item xs={12} sm={6} key={index}>
                      <CardMedia
                        component="img"
                        src={`${API_URL}${img}`}
                        sx={{
                          width: 200,
                          height: 200,
                          objectFit: "cover",
                          borderRadius: 2,
                          boxShadow: "0 4px 12px rgba(0,0,0,0.1)",
                        }}
                        alt={`Article image ${index + 1}`}
                      />
                    </Grid>
                  ))}
                </Grid>
              </Box>
            )}

          {/* Article Content */}
          <Box sx={{ mb: 3 }}>
            <Typography
              variant="body1"
              sx={{
                lineHeight: 1.8,
                fontSize: { xs: "1rem", sm: "1.1rem" },
                whiteSpace: "pre-wrap",
                wordBreak: "break-word",
                color: colors.darkBlack,
              }}
            >
              {selectedArticleDetail?.description}
            </Typography>
          </Box>

          {/* WordPress Link Section */}
          {selectedArticleDetail?.link && (
            <Box
              sx={{
                mt: 3,
                p: 2,
                backgroundColor: colors.lightMint,
                borderRadius: 2,
                border: `1px solid ${colors.green}20`,
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", mb: 1 }}>
                <LinkIcon sx={{ mr: 1, color: colors.green }} />
                <Typography
                  variant="subtitle1"
                  sx={{
                    fontWeight: 600,
                    color: colors.darkGreen,
                    fontSize: { xs: "1rem", sm: "1.1rem" },
                  }}
                >
                  WordPress Link
                </Typography>
              </Box>
              <MuiLink
                href={selectedArticleDetail.link}
                target="_blank"
                rel="noopener noreferrer"
                sx={{
                  color: colors.blue,
                  textDecoration: "none",
                  fontSize: { xs: "0.9rem", sm: "1rem" },
                  wordBreak: "break-all",
                  "&:hover": {
                    textDecoration: "underline",
                    color: colors.darkBlue,
                  },
                }}
              >
                {selectedArticleDetail.link}
              </MuiLink>
            </Box>
          )}

          {/* Article Metadata */}
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              mt: 4,
              pt: 3,
              borderTop: `1px solid ${colors.lightGray}`,
              flexWrap: "wrap",
              gap: 1,
            }}
          >
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: { xs: "0.8rem", sm: "0.9rem" } }}
            >
              Published on {formatDate(selectedArticleDetail?.createdAt)}
            </Typography>

            <Button
              variant="outlined"
              size="small"
              onClick={() => setDetailModalOpen(false)}
              sx={{
                borderColor: colors.green,
                color: colors.green,
                "&:hover": {
                  backgroundColor: "rgba(76, 175, 80, 0.1)",
                  borderColor: colors.darkGreen,
                },
              }}
            >
              Close
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Action Menu */}
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        PaperProps={{
          sx: {
            borderRadius: 2,
            boxShadow: "0 8px 24px rgba(0,0,0,0.12)",
            minWidth: 140,
          },
        }}
      >
        <MenuItem onClick={handleEditClick}>
          <ListItemIcon>
            <Edit fontSize="small" />
          </ListItemIcon>
          <ListItemText>Edit</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleDeleteClick} sx={{ color: colors.red }}>
          <ListItemIcon>
            <Delete fontSize="small" sx={{ color: colors.red }} />
          </ListItemIcon>
          <ListItemText>Delete</ListItemText>
        </MenuItem>
      </Menu>

      {/* Add Article Dialog */}
      <Dialog
        open={openDialog}
        onClose={() => {
          if (!dialogLoading) {
            setOpenDialog(false);
            resetForm();
          }
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
            >
              Add New Article
            </Typography>
            <IconButton
              onClick={() => {
                if (!dialogLoading) {
                  setOpenDialog(false);
                  resetForm();
                }
              }}
              disabled={dialogLoading}
              size={isMobile ? "small" : "medium"}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Article Title"
              name="title"
              value={formData.title}
              onChange={handleInputChange}
              required
              disabled={dialogLoading}
              sx={{ mb: 3 }}
              size={isMobile ? "small" : "medium"}
            />

            <TextField
              fullWidth
              label="Article Description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              multiline
              rows={isMobile ? 3 : 4}
              disabled={dialogLoading}
              sx={{ mb: 3 }}
              size={isMobile ? "small" : "medium"}
            />

            {/* WordPress Link Field */}
            <TextField
              fullWidth
              label="WordPress Link (Optional)"
              name="link"
              value={formData.link}
              onChange={handleInputChange}
              placeholder="https://wordpress.com/..."
              disabled={dialogLoading}
              sx={{ mb: 3 }}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: <LinkIcon sx={{ mr: 1, color: colors.green }} />,
              }}
              helperText="Enter the full URL to your WordPress article"
            />

            {/* Multiple Image Upload Section */}
            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                disabled={dialogLoading || imagePreviews.length >= 5}
                startIcon={<ImageIcon />}
                sx={{ mb: 1 }}
                size={isMobile ? "small" : "medium"}
              >
                {imagePreviews.length > 0
                  ? `Add More Images (${imagePreviews.length}/5)`
                  : "Upload Images (Max 5)"}
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleImageChange}
                  disabled={dialogLoading || imagePreviews.length >= 5}
                />
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem" } }}
              >
                {imagePreviews.length >= 5
                  ? "Maximum 5 images reached"
                  : `Select up to ${5 - imagePreviews.length} more images`}
              </Typography>

              {/* Image Previews */}
              {imagePreviews.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                    Selected Images ({imagePreviews.length}/5):
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {imagePreviews.map((preview, index) => (
                      <Box key={index} sx={{ position: "relative" }}>
                        <CardMedia
                          component="img"
                          src={preview}
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 1,
                            objectFit: "cover",
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            bgcolor: "white",
                            boxShadow: 1,
                          }}
                          onClick={() => removeImage(index)}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            <DialogActions
              sx={{
                px: 0,
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1, sm: 0 },
              }}
            >
              <Button
                onClick={() => {
                  if (!dialogLoading) {
                    setOpenDialog(false);
                    resetForm();
                  }
                }}
                disabled={dialogLoading}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  dialogLoading || !formData.title || !formData.description
                }
                sx={{
                  background: colors.greenGradient,
                  "&:hover": { background: colors.greenGradientAlt },
                  minWidth: 100,
                  width: { xs: "100%", sm: "auto" },
                }}
                size={isMobile ? "small" : "medium"}
              >
                {dialogLoading ? <CircularProgress size={20} /> : "Post"}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Edit Article Dialog */}
      <Dialog
        open={editDialogOpen}
        onClose={() => {
          if (!dialogLoading) {
            setEditDialogOpen(false);
            resetEditForm();
          }
        }}
        maxWidth="md"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Box
            sx={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <Typography
              variant="h6"
              fontWeight="bold"
              sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
            >
              Edit Article
            </Typography>
            <IconButton
              onClick={() => {
                if (!dialogLoading) {
                  setEditDialogOpen(false);
                  resetEditForm();
                }
              }}
              disabled={dialogLoading}
              size={isMobile ? "small" : "medium"}
            >
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>

        <DialogContent>
          <Box component="form" onSubmit={handleEditSubmit} sx={{ mt: 2 }}>
            <TextField
              fullWidth
              label="Article Title"
              name="title"
              value={editFormData.title}
              onChange={handleEditInputChange}
              required
              disabled={dialogLoading}
              sx={{ mb: 3 }}
              size={isMobile ? "small" : "medium"}
            />

            <TextField
              fullWidth
              label="Article Description"
              name="description"
              value={editFormData.description}
              onChange={handleEditInputChange}
              required
              multiline
              rows={isMobile ? 3 : 4}
              disabled={dialogLoading}
              sx={{ mb: 3 }}
              size={isMobile ? "small" : "medium"}
            />

            {/* WordPress Link Field for Edit */}
            <TextField
              fullWidth
              label="WordPress Link (Optional)"
              name="link"
              value={editFormData.link}
              onChange={handleEditInputChange}
              placeholder="https://wordpress.com/..."
              disabled={dialogLoading}
              sx={{ mb: 3 }}
              size={isMobile ? "small" : "medium"}
              InputProps={{
                startAdornment: <LinkIcon sx={{ mr: 1, color: colors.green }} />,
              }}
              helperText="Enter the full URL to your WordPress article"
            />

            {/* Multiple Image Upload Section for Edit */}
            <Box sx={{ mb: 3 }}>
              <Button
                variant="outlined"
                component="label"
                fullWidth
                disabled={dialogLoading || editImagePreviews.length >= 5}
                startIcon={<ImageIcon />}
                sx={{ mb: 1 }}
                size={isMobile ? "small" : "medium"}
              >
                {editImagePreviews.length > 0
                  ? `Add More Images (${editImagePreviews.length}/5)`
                  : "Upload New Images (Max 5)"}
                <input
                  type="file"
                  hidden
                  multiple
                  accept="image/*"
                  onChange={handleEditImageChange}
                  disabled={dialogLoading || editImagePreviews.length >= 5}
                />
              </Button>
              <Typography
                variant="caption"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem" } }}
              >
                {editImagePreviews.length >= 5
                  ? "Maximum 5 images reached"
                  : `Select up to ${5 - editImagePreviews.length} more images`}
              </Typography>

              {/* New Images Previews */}
              {editImagePreviews.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>
                    New Images ({editImagePreviews.length}/5):
                  </Typography>
                  <Box sx={{ display: "flex", gap: 1, flexWrap: "wrap" }}>
                    {editImagePreviews.map((preview, index) => (
                      <Box key={index} sx={{ position: "relative" }}>
                        <CardMedia
                          component="img"
                          src={preview}
                          sx={{
                            width: 80,
                            height: 80,
                            borderRadius: 1,
                            objectFit: "cover",
                          }}
                        />
                        <IconButton
                          size="small"
                          sx={{
                            position: "absolute",
                            top: -8,
                            right: -8,
                            bgcolor: "white",
                            boxShadow: 1,
                          }}
                          onClick={() => removeEditImage(index)}
                        >
                          <Close fontSize="small" />
                        </IconButton>
                      </Box>
                    ))}
                  </Box>
                </Box>
              )}
            </Box>

            <DialogActions
              sx={{
                px: 0,
                flexDirection: { xs: "column", sm: "row" },
                gap: { xs: 1, sm: 0 },
              }}
            >
              <Button
                onClick={() => setEditDialogOpen(false)}
                disabled={dialogLoading}
                fullWidth={isMobile}
                size={isMobile ? "small" : "medium"}
              >
                Cancel
              </Button>
              <Button
                type="submit"
                variant="contained"
                disabled={
                  dialogLoading ||
                  !editFormData.title ||
                  !editFormData.description
                }
                sx={{
                  background: colors.greenGradient,
                  "&:hover": { background: colors.greenGradientAlt },
                  minWidth: 100,
                  width: { xs: "100%", sm: "auto" },
                }}
                size={isMobile ? "small" : "medium"}
              >
                {dialogLoading ? <CircularProgress size={20} /> : "Update"}
              </Button>
            </DialogActions>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteDialogOpen}
        onClose={() => {
          if (!dialogLoading) {
            setDeleteDialogOpen(false);
            setSelectedArticle(null);
          }
        }}
        maxWidth="xs"
        fullWidth
        fullScreen={isMobile}
      >
        <DialogTitle>
          <Typography
            variant="h6"
            fontWeight="bold"
            sx={{ fontSize: { xs: "1.1rem", sm: "1.25rem" } }}
          >
            Confirm Delete
          </Typography>
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ fontSize: { xs: "0.9rem", sm: "1rem" } }}>
            Are you sure you want to delete the article "
            {selectedArticle?.title}"? This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions
          sx={{
            flexDirection: { xs: "column", sm: "row" },
            gap: { xs: 1, sm: 0 },
          }}
        >
          <Button
            onClick={() => {
              setDeleteDialogOpen(false);
              setSelectedArticle(null);
            }}
            disabled={dialogLoading}
            fullWidth={isMobile}
            size={isMobile ? "small" : "medium"}
          >
            Cancel
          </Button>
          <Button
            onClick={handleDeleteArticle}
            variant="contained"
            disabled={dialogLoading}
            sx={{
              background: colors.red,
              "&:hover": { background: colors.darkRed },
              width: { xs: "100%", sm: "auto" },
            }}
            size={isMobile ? "small" : "medium"}
          >
            {dialogLoading ? <CircularProgress size={20} /> : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Articles;